import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import type { PanelPersonal } from '../../../domain/entities/PanelPersonal';
import estilos from './Panel.module.css';

interface SelectorPanelProps {
  paneles: readonly PanelPersonal[];
  activoId: string;
  /** Sólo en edición se pueden crear y borrar. */
  editando: boolean;
  onAbrir: (panelId: string) => void;
  onCrear: (nombre: string) => void;
  onBorrar: (panelId: string) => void;
}

/**
 * El nombre de la instalación, convertido en selector de panel.
 *
 * Una persona puede tener varias vistas —la nave, la oficina, el perímetro— y
 * cambiar entre ellas debe costar un toque. Va sobre el nombre y no en un botón
 * aparte porque el nombre ya dice cuál estás viendo: pulsarlo para ver los demás
 * es el gesto que uno espera.
 *
 * Crear y borrar sólo aparecen en edición: son cambios de estructura y no deben
 * quedar a un clic de distancia mientras alguien vigila.
 */
export function SelectorPanel({
  paneles,
  activoId,
  editando,
  onAbrir,
  onCrear,
  onBorrar,
}: SelectorPanelProps) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement | null>(null);

  const activo = paneles.find((p) => p.id === activoId) ?? paneles[0] ?? null;

  useEffect(() => {
    if (!abierto) return;

    const alPulsarFuera = (evento: MouseEvent) => {
      if (!contenedor.current?.contains(evento.target as Node)) setAbierto(false);
    };

    const alPulsarTecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierto(false);
    };

    document.addEventListener('mousedown', alPulsarFuera);
    document.addEventListener('keydown', alPulsarTecla);

    return () => {
      document.removeEventListener('mousedown', alPulsarFuera);
      document.removeEventListener('keydown', alPulsarTecla);
    };
  }, [abierto]);

  const crear = () => {
    const nombre = window.prompt('¿Cómo se llama el panel nuevo?', 'Panel nuevo');
    if (nombre === null) return;

    setAbierto(false);
    onCrear(nombre);
  };

  return (
    <div ref={contenedor} className={estilos.selectorPanel}>
      <button
        type="button"
        className={estilos.nombreSitio}
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        aria-haspopup="menu"
        title="Cambiar de panel"
      >
        {activo?.nombreInstalacion ?? 'Panel'}
        <Icon nombre={abierto ? 'contraer' : 'expandir'} tamano={14} />
      </button>

      {abierto && (
        <div className={estilos.listaPaneles} role="menu">
          {paneles.map((p) => (
            <div key={p.id} className={estilos.filaPanel}>
              <button
                type="button"
                className={[
                  estilos.opcionPanel,
                  p.id === activoId ? estilos.opcionPanelActiva : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => {
                  setAbierto(false);
                  onAbrir(p.id);
                }}
              >
                {p.id === activoId && <Icon nombre="check" tamano={14} />}
                <span>{p.nombreInstalacion}</span>
              </button>

              {/* Nunca se ofrece borrar el último: la pantalla necesita uno. */}
              {editando && paneles.length > 1 && (
                <button
                  type="button"
                  className={estilos.borrarPanel}
                  title={`Borrar «${p.nombreInstalacion}»`}
                  aria-label={`Borrar ${p.nombreInstalacion}`}
                  onClick={() => {
                    const seguro = window.confirm(
                      `¿Borrar el panel «${p.nombreInstalacion}»?\n\n` +
                        'Se pierden sus marcadores y tarjetas. Los demás paneles no se tocan.',
                    );
                    if (seguro) {
                      setAbierto(false);
                      onBorrar(p.id);
                    }
                  }}
                >
                  <Icon nombre="eliminar" tamano={14} />
                </button>
              )}
            </div>
          ))}

          {editando && (
            <button type="button" className={estilos.opcionPanel} onClick={crear}>
              <Icon nombre="agregar" tamano={14} />
              <span>Panel nuevo</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
